import { DatePipe, NgClass } from '@angular/common';
import { Component, effect, inject, input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';

import { Logger } from '../../../core/logging/logger.service';
import {
  NumeronCollectionModel,
  NumeronItemModel,
} from '../../../features/numerons/models/numeron.model';
import { NumeronsService } from '../../../features/numerons/services/numerons.service';
import { panelSubtypeClasses } from '../../utils/panel-subtype-class-map';

@Component({
  selector: 'app-collection-list-card',
  imports: [DatePipe, NgClass, RouterLink],
  templateUrl: './collection-list-card.component.html',
})
export class CollectionListCardComponent {
  public readonly collection = input.required<NumeronCollectionModel>();
  public readonly showCollectionTitle = input(false);
  public readonly showManagementActions = input(false);
  private readonly numeronsService = inject(NumeronsService);
  private readonly logger = inject(Logger);
  private readonly router = inject(Router);
  protected readonly collectionLikes = signal(0);
  protected readonly isCopying = signal(false);
  protected readonly copyErrorMessage = signal('');

  public constructor() {
    effect(() => {
      this.collectionLikes.set(this.collection().likes ?? 0);
    });
  }

  protected byPublisherHref(userId?: string): string {
    return userId ? `/numerons/bypublisher/${userId}` : '/';
  }

  protected byTopicHref(topicId?: string): string {
    return topicId ? `/numerons/bytopic/${topicId}` : '/';
  }

  protected byCategoryHref(category: string): string {
    return `/numerons/bycategory/${encodeURIComponent(category)}`;
  }

  protected hasExternalSource(url?: string): boolean {
    return typeof url === 'string' && url.trim().length > 0;
  }

  protected collectionDisplayImage(collection: NumeronCollectionModel): string | undefined {
    if (collection.assetPath && collection.assetPath.trim().length > 0) {
      return collection.assetPath;
    }

    return collection.numerons[0]?.assetPath;
  }

  protected hasNumerons(collection: NumeronCollectionModel): boolean {
    return collection.numerons.length > 0;
  }

  protected listItemHasLinkedTitle(numeron: NumeronItemModel): boolean {
    return numeron.source === 'Numerical' && this.hasExternalSource(numeron.url);
  }

  protected isImageNumeron(numeron: NumeronItemModel): boolean {
    return Number(numeron.type) === 0;
  }

  protected collectionEmailHref(collection: NumeronCollectionModel): string {
    const email = collection.user?.email;
    if (!email || email.trim().length === 0) {
      return '#';
    }

    const subject = encodeURIComponent(collection.title);
    return `mailto:${email}?subject=${subject}`;
  }

  protected hasCollectionEmail(collection: NumeronCollectionModel): boolean {
    const email = collection.user?.email;
    return typeof email === 'string' && email.trim().length > 0;
  }

  protected panelSubtypeClasses(subType?: string): string {
    return panelSubtypeClasses(subType);
  }

  protected addNumeronHref(collectionId: string): string {
    return `/mynumerons/collection/${collectionId}/addnumeron`;
  }

  protected updateNumeronHref(collectionId: string, numeronId: string): string {
    return `/mynumerons/numeron/${collectionId}/${numeronId}/update`;
  }

  protected deleteNumeronHref(collectionId: string, numeronId: string): string {
    return `/mynumerons/collection/${collectionId}/numeron/${numeronId}/delete`;
  }

  protected editCollectionHref(collection: NumeronCollectionModel): string {
    return `/mynumerons/collection/${collection._id}/edit`;
  }

  protected editCollectionQueryParams(collection: NumeronCollectionModel): { returnTo: string } {
    return {
      returnTo: `/mynumerons/collection/${collection._id}`,
    };
  }

  protected deleteCollectionHref(collection: NumeronCollectionModel): string {
    return `/mynumerons/${collection._id}/delete`;
  }

  protected deleteCollectionQueryParams(collection: NumeronCollectionModel): {
    returnTo: string;
    successReturnTo: string;
    itemTitle: string;
  } {
    return {
      returnTo: `/mynumerons/collection/${collection._id}`,
      successReturnTo: '/mynumerons',
      itemTitle: collection.title?.trim() || '',
    };
  }

  protected async copyCollection(): Promise<void> {
    if (this.isCopying()) {
      return;
    }

    this.isCopying.set(true);
    this.copyErrorMessage.set('');

    this.numeronsService
      .copyCollection(this.collection()._id)
      .pipe(take(1))
      .subscribe({
        next: async (collection) => {
          this.isCopying.set(false);
          await this.router.navigate(['/mynumerons/collection', collection._id], {
            queryParams: {
              returnTo: '/mynumerons',
            },
          });
        },
        error: (error) => {
          this.isCopying.set(false);
          this.copyErrorMessage.set('Oops, could not copy Collection!');
          this.logger.warn('Failed to copy collection.', {
            feature: 'numerons',
            operation: 'copy-collection',
            severity: 'medium',
            error,
          });
        },
      });
  }

  protected likeCollection(): void {
    this.numeronsService
      .likeCollection(this.collection()._id)
      .pipe(take(1))
      .subscribe({
        next: (response: unknown) => {
          const nModified = Number(
            (response as { nModified?: number | string; result?: { nModified?: number | string } })
              ?.nModified ??
              (response as { result?: { nModified?: number | string } })?.result?.nModified ??
              0,
          );

          if (nModified === 1) {
            this.collectionLikes.update((currentLikes) => currentLikes + 1);
          }
        },
        error: (error) => {
          this.logger.warn('Failed to like collection.', {
            feature: 'numerons',
            operation: 'like-collection',
            severity: 'medium',
            error,
          });
        },
      });
  }
}
